/* eslint-disable @angular-eslint/directive-selector */
import {
  Directive,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { FeatureFacade } from '../services/feature.facade';

/**
 * Directive that checks whether the feature is enabled or not.
 * By default the feature flag hasn't been defined, it will create the component.
 *
 * @export
 * @class FeatureToggleDirective
 */
@Directive({
  selector: '[featureToggle]',
})
export class FeatureToggleDirective implements OnDestroy {
  static ngTemplateGuard_featureToggle: 'binding';

  private destroy$ = new Subject<boolean>();
  private elseTemplateRef: TemplateRef<any> | null = null;
  private featChanged$ = new Subject<string>();
  private isFeatEnabled = true;

  isFeatEnabled$ = this.featChanged$.pipe(
    switchMap((featId) => this.featureFacade.isFeatureEnabled(featId))
  );

  constructor(
    private viewContainer: ViewContainerRef,
    private templateRef: TemplateRef<any>,
    private featureFacade: FeatureFacade
  ) {
    this.isFeatEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((featEnabled) => {
        this.isFeatEnabled = featEnabled;
        this.updateView();
      });
  }

  /**
   * The feature Id. Value comes from the Features namespace
   *
   * @memberof FeatureToggleDirective
   */
  @Input()
  set featureToggle(featId: string) {
    this.featChanged$.next(featId);
  }

  /**
   * Specify Else template when feature is not enabled
   *
   * @type {TemplateRef<any>}
   * @memberof FeatureToggleDirective
   */
  @Input()
  set featureToggleElse(templateRef: TemplateRef<any> | null) {
    this.elseTemplateRef = templateRef;
    this.updateView();
  }

  /**
   * TODO: Need to implement logic that checks whether a feature
   * should be enabled based on Insurance Contract Id
   *
   * @type {string}
   * @memberof FeatureToggleDirective
   */
  @Input() insuranceContractId!: string;

  /**
   * TODO: Need to implement logic that checks whether a feature
   * should be enaled based on Plan Id
   *
   * @type {string}
   * @memberof FeatureToggleDirective
   */
  @Input() planId!: string;

  /**
   * Since we are manually subscribing to actions and selectors, we need to unsubscribe
   * The cleanest approach is the use the takeUntil operator.
   * On ngOnDestroy, we simply complete the destroy subject and this should take care of zombie subscribers.
   *
   * @memberof FeatureToggleDirective
   */
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateView(): void {
    if (this.isFeatEnabled) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      this.viewContainer.clear();
      if (this.elseTemplateRef) {
        this.viewContainer.createEmbeddedView(this.elseTemplateRef);
      }
    }
  }
}
